import { Badge, cn, useOverflowTabs } from '@seen/ui'
import type { JSX } from 'react'
import { useLocation, useParams } from 'react-router'
import { NavigationMenu } from '#src/components/core/navigation-menu/NavigationMenu.tsx'
import { NavigationMenuContent } from '#src/components/core/navigation-menu/NavigationMenuContent.tsx'
import { NavigationMenuItem } from '#src/components/core/navigation-menu/NavigationMenuItem.tsx'
import { NavigationMenuLink } from '#src/components/core/navigation-menu/NavigationMenuLink.tsx'
import { NavigationMenuList } from '#src/components/core/navigation-menu/NavigationMenuList.tsx'
import { NavigationMenuTrigger } from '#src/components/core/navigation-menu/NavigationMenuTrigger.tsx'
import type { PatientNavigationItem } from '#src/hooks/usePatientNavigationItems.ts'

export function PatientNavigation({
  className,
  navigationItems,
}: {
  className?: string
  navigationItems: PatientNavigationItem[]
}): JSX.Element {
  const { containerRef, measurerRef, state } = useOverflowTabs({
    count: navigationItems.length,
    minVisible: 1,
    moreButtonWidth: 80,
  })
  // Items with an `activeSegment` (e.g. Notes) stay highlighted across their
  // sibling routes; NavLink only matches its own `to`, so force the `active`
  // class. Match the segment in its expected slot right after the patient id
  // (/Patient/:id/<segment>/…) — not a position-agnostic includes() that any
  // stray segment of the same name could trip — so every notes view highlights
  // Notes.
  const { pathname } = useLocation()
  const { patientId } = useParams()
  const isSegmentActive = (segment: string | undefined): boolean =>
    segment != null &&
    (pathname === `/Patient/${patientId}/${segment}` ||
      pathname.startsWith(`/Patient/${patientId}/${segment}/`))

  return (
    <div
      ref={containerRef}
      className={cn('w-full px-pagex-default lg:px-pagex-lg', className)}
    >
      <div
        ref={measurerRef}
        className="pointer-events-none invisible absolute flex gap-4 lg:gap-6"
        aria-hidden="true"
      >
        {navigationItems.map(({ label, indicator }) => (
          <span
            key={`measure-${label}`}
            className="flex items-center gap-1.5 text-sm font-medium whitespace-nowrap"
          >
            {label}
            {indicator != null && indicator > 0 && (
              <Badge
                variant="outline"
                color="blue"
                className="min-w-5 justify-center"
              >
                {indicator}
              </Badge>
            )}
          </span>
        ))}
      </div>

      <NavigationMenu
        className="w-full justify-start"
        delayDuration={0}
        viewportClassName="mt-0 z-50"
      >
        <NavigationMenuList
          className={cn(
            'gap-4 lg:gap-6',
            'ml-px' // Nudge over for visual alignment.
          )}
        >
          {navigationItems
            .slice(0, state.visibleCount)
            .map(({ label, to, indicator, activeSegment }) => (
              // Key on the stable label, not `to` — the Notes item's `to` flips
              // between its views, and keying on it would remount (flash + reset
              // transitions) the tab on every view switch.
              <NavigationMenuItem key={label}>
                <NavigationMenuLink
                  to={to}
                  className={cn(
                    'py-pagex-default lg:py-pagex-lg',
                    isSegmentActive(activeSegment) && 'active'
                  )}
                  end={to === ''}
                >
                  <span className="flex items-center gap-1.5 whitespace-nowrap">
                    {label}
                    {indicator != null && indicator > 0 && (
                      <Badge
                        variant="outline"
                        color="blue"
                        className="min-w-5 justify-center"
                      >
                        {indicator}
                      </Badge>
                    )}
                  </span>
                </NavigationMenuLink>
              </NavigationMenuItem>
            ))}
          {state.isOverflowing && (
            <NavigationMenuItem>
              <NavigationMenuTrigger
                className="-mx-4 opacity-40 hover:opacity-100 data-[state=open]:opacity-100"
                hideIcon
              >
                {navigationItems.length - state.visibleCount} more…
              </NavigationMenuTrigger>
              <NavigationMenuContent className="flex flex-col p-2">
                {navigationItems
                  .slice(state.visibleCount)
                  .map(({ label, to, indicator, activeSegment }) => (
                    <NavigationMenuLink
                      key={label}
                      to={to}
                      className={cn(
                        'w-44 px-2 py-1.5',
                        isSegmentActive(activeSegment) && 'active'
                      )}
                      end={to === ''}
                    >
                      <span className="flex items-center justify-between gap-1.5">
                        {label}
                        {indicator != null && indicator > 0 && (
                          <Badge
                            variant="outline"
                            color="blue"
                            className="min-w-5 justify-center"
                          >
                            {indicator}
                          </Badge>
                        )}
                      </span>
                    </NavigationMenuLink>
                  ))}
              </NavigationMenuContent>
            </NavigationMenuItem>
          )}
        </NavigationMenuList>
      </NavigationMenu>
    </div>
  )
}
